// vs-comparison Remotion composition：左右对决，高亮 stat 逐对平滑切换。
// 每条 stat 的强调强度按时间脉冲（前 0.35s 淡入、后 0.35s 淡出），相邻 stat 在
// 切换点交叉过渡，而非瞬间跳变。frame 0 stat0 已全亮（暂停可见）。
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import type { VsData } from './types'
import type { CompositionProps } from '../../remotion/ScenePlayer'
import { THEME } from '../../remotion/theme'

const STEP = 2.5 // 每 stat 高亮时长（秒）
const FADE = 0.35 // 交叉淡入淡出窗口

/** stat i 在时间 t 的强调强度 0..1（脉冲：淡入→保持→淡出）。 */
function statIntensity(i: number, t: number): number {
  const start = i * STEP
  const end = (i + 1) * STEP
  const up = interpolate(t, [start - FADE, start], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
  const down = interpolate(t, [end - FADE, end], [1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
  return Math.min(up, down)
}

const Side: React.FC<{
  name?: string
  stats: { label: string; value: string }[]
  t: number
  accent: string
  align: 'left' | 'right'
}> = ({ name, stats, t, accent, align }) => {
  const pad = align === 'left' ? { paddingLeft: 56, paddingRight: 24 } : { paddingRight: 56, paddingLeft: 24 }
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: align === 'left' ? 'flex-start' : 'flex-end', ...pad }}>
      {name && <div style={{ color: THEME.w, fontSize: 64, fontWeight: 800, marginBottom: 40 }}>{name}</div>}
      {stats.map((s, i) => {
        const k = statIntensity(i, t) // 0..1 强调强度
        return (
          <div key={i} style={{
            marginBottom: 28,
            opacity: 0.45 + 0.55 * k,
            transform: `scale(${1 + 0.08 * k})`,
            transformOrigin: align === 'left' ? 'left center' : 'right center',
            textAlign: align,
          }}>
            <div style={{ color: k > 0.5 ? accent : THEME.t2, fontSize: 34, fontWeight: 600 }}>{s.label}</div>
            <div style={{ color: k > 0.5 ? THEME.w : THEME.t, fontSize: 56 + 16 * k, fontWeight: 800, fontFamily: THEME.fontMono }}>
              {s.value}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const VsComparisonComposition: React.FC<CompositionProps> = ({ data }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const d = data as unknown as VsData
  const t = frame / fps

  return (
    <AbsoluteFill style={{ background: (data as any).bgColor || THEME.bg, fontFamily: THEME.fontSans }}>
      {d.title && (
        <div style={{ position: 'absolute', top: 110, left: 0, right: 0, textAlign: 'center', color: THEME.w, fontWeight: 800, fontSize: 70 }}>
          {d.title}
        </div>
      )}
      <div style={{ position: 'absolute', top: 260, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'flex-start' }}>
        <Side name={d.left?.name} stats={d.left?.stats || []} t={t} accent={THEME.p} align="left" />
        <div style={{
          position: 'absolute', left: '50%', top: 40, transform: 'translateX(-50%)',
          color: '#fff', fontSize: 64, fontWeight: 800, fontFamily: THEME.fontMono,
          background: `linear-gradient(135deg, ${THEME.p}, ${THEME.pHover})`,
          borderRadius: 24, padding: '14px 36px',
          boxShadow: `0 8px 32px ${THEME.p}44`,
        }}>
          VS
        </div>
        <Side name={d.right?.name} stats={d.right?.stats || []} t={t} accent="#f59e0b" align="right" />
      </div>
    </AbsoluteFill>
  )
}
